'use client';

import * as React from 'react';
import { ParticipantTile, useMaybeTrackRefContext } from '@livekit/components-react';
import { ParticipantEvent } from 'livekit-client';
import { parseHandRaisedAt } from './mediaPolicy';

/**
 * `ParticipantTile` with a raised-hand badge.
 *
 * Why a wrapper rather than children: the upstream tile renders
 * `children ?? <default>`, so passing children REPLACES the entire default body
 * — video, visualizer, and the name/mute metadata block. Wrapping leaves the
 * stock tile untouched and layers a badge over it.
 *
 * The wrapper becomes the grid/carousel item in place of the tile, so
 * `.lk-hand-raised-wrapper` in globals.css re-establishes the sizing the tile
 * used to get for free as a direct child (see the rule there — it is
 * load-bearing, not cosmetic).
 *
 * Drop-in for `<ParticipantTile />` as the single child of GridLayout or
 * CarouselLayout: both clone their child per track and supply TrackRefContext,
 * so the track ref arrives through context with no prop drilling.
 *
 * Does NOT cover FocusLayout — that renders its own tile internally and takes no
 * children, so the pinned/screenshare participant shows no badge. Their raised
 * hand is still visible in the roster and on their carousel tile.
 */
export function CustomParticipantTile(
  props: React.ComponentProps<typeof ParticipantTile>,
) {
  const contextTrackRef = useMaybeTrackRefContext();
  const trackRef = props.trackRef ?? contextTrackRef;
  const participant = trackRef?.participant;

  // Attributes aren't reactive on their own, so re-render on this participant's
  // own AttributesChanged event. Scoped per-participant rather than a
  // room-level listener so one raised hand doesn't re-render every tile.
  const [handRaised, setHandRaised] = React.useState(false);

  React.useEffect(() => {
    if (!participant) {
      setHandRaised(false);
      return;
    }
    const sync = () =>
      setHandRaised(parseHandRaisedAt(participant.attributes) !== undefined);
    sync();
    participant.on(ParticipantEvent.AttributesChanged, sync);
    return () => {
      participant.off(ParticipantEvent.AttributesChanged, sync);
    };
  }, [participant]);

  return (
    <div className="lk-hand-raised-wrapper">
      <ParticipantTile {...props} trackRef={trackRef} />
      {handRaised && (
        <div className="lk-hand-raised-badge" title="Hand raised" aria-label="Hand raised">
          <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 16 16" fill="none">
            <path
              fill="currentColor"
              d="M7 1.5a1 1 0 0 1 2 0V7h.5V2.75a1 1 0 0 1 2 0V7h.5V4.5a1 1 0 0 1 2 0v5a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V6.5a1 1 0 0 1 2 0V9h.5a.5.5 0 0 0 .5-.5V1.5Z"
            />
          </svg>
        </div>
      )}
    </div>
  );
}
